import { callFunction } from "./functions-client";

// Fleet-OS F5 — maintenance & diagnostics. Preventive schedules (by miles / days /
// engine-hours), a service log, and fault-code (DTC) tracking, per vehicle. Owner-private.
export const INTERVAL_TYPES = ["miles", "days", "engine_hours"] as const;
export type IntervalType = (typeof INTERVAL_TYPES)[number];

export const INTERVAL_LABELS: Record<IntervalType, string> = {
  miles: "miles", days: "days", engine_hours: "engine hrs",
};

export type DueStatus = "ok" | "due_soon" | "overdue" | "unknown";
export const DUE_LABELS: Record<DueStatus, string> = {
  ok: "OK", due_soon: "Due soon", overdue: "Overdue", unknown: "No reading",
};

export const SEVERITY_LABELS: Record<number, string> = { 1: "Info", 2: "Warning", 3: "Stop" };

export interface Schedule {
  id: string; vehicle_id: string; task: string;
  interval_type: IntervalType; interval_value: number;
  last_done_at: string | null; due_status: DueStatus; remaining: number | null;
}
export interface ServiceRecord {
  id: string; vehicle_id: string; schedule_id: string | null; task: string;
  performed_at: string; odometer: number | null; engine_hours: number | null;
  cost_cents: number | null; vendor: string | null; notes: string | null;
}
export interface FaultCode {
  id: string; vehicle_id: string; code: string; description: string | null;
  severity: number; source: string; resolved: boolean; reported_at: string;
}

export const createSchedule = (input: {
  vehicle_id: string; task: string; interval_type: IntervalType; interval_value: number;
  last_done_at?: string; last_done_odometer?: number; last_done_hours?: number;
}) => callFunction<{ id: string }>("maintenance/schedule-create", { body: input });

export const listSchedules = (vehicle_id?: string) =>
  callFunction<{ schedules: Schedule[] }>(`maintenance/schedules${vehicle_id ? `?vehicle_id=${encodeURIComponent(vehicle_id)}` : ""}`, { method: "GET" });

export const deleteSchedule = (id: string) =>
  callFunction<{ ok: boolean }>("maintenance/schedule-delete", { body: { id } });

export const addRecord = (input: {
  vehicle_id: string; schedule_id?: string; task: string; performed_at?: string;
  odometer?: number; engine_hours?: number; cost_cents?: number; vendor?: string; notes?: string;
}) => callFunction<{ id: string }>("maintenance/record-add", { body: input });

export const listRecords = (vehicle_id?: string) =>
  callFunction<{ records: ServiceRecord[] }>(`maintenance/records${vehicle_id ? `?vehicle_id=${encodeURIComponent(vehicle_id)}` : ""}`, { method: "GET" });

export const setMeter = (input: { vehicle_id: string; odometer?: number; engine_hours?: number }) =>
  callFunction<{ ok: boolean }>("maintenance/meter", { body: input });

export const reportFault = (input: { vehicle_id: string; code: string; description?: string; severity?: number; source?: "manual" | "obd" }) =>
  callFunction<{ id: string }>("maintenance/dtc-report", { body: input });

export const listFaults = (vehicle_id?: string, openOnly = false) => {
  const p = new URLSearchParams();
  if (vehicle_id) p.set("vehicle_id", vehicle_id);
  if (openOnly) p.set("open", "1");
  const qs = p.toString();
  return callFunction<{ dtcs: FaultCode[] }>(`maintenance/dtcs${qs ? `?${qs}` : ""}`, { method: "GET" });
};

export const resolveFault = (id: string) =>
  callFunction<{ ok: boolean }>("maintenance/dtc-resolve", { body: { id } });
